/** @jsx jsx */
/**
 * Customizations:
 * - Add 'largest' image.
 * - Show as much of the bottle as possible.
 */
import { gql } from '@apollo/client';
import { BottleImagePlaceholder } from '@bottlebooks/gatsby-theme-base';
import BaseProductImage from '@bottlebooks/gatsby-theme-event/src/components/Product/ProductImage';
import { getFixedImage } from '@bottlebooks/gatsby-theme-event/src/helpers/getImageProps';
import { graphql } from 'gatsby';
import Image from 'gatsby-image';
import { jsx } from 'theme-ui';

export * from '@bottlebooks/gatsby-theme-event/src/components/Product/ProductImage';

// Using a trick here:
// On mobile the image is displayed completely,
// but on larger screens I show only the middle section.
// This cuts off parts of very tall bottleImages,
// but I compensate that by positioning it to the top center, so the top isn't cut off.
const dimensions = {
  largest: {
    width: [200, 150],
    height: [300, 600],
  },
};
const positions = {
  largest: {
    img: {
      objectFit: ['contain', 'cover'],
      objectPosition: ['center', 'center top'],
    },
  },
};

export default function ProductImage({
  product,
  size = 'default',
  className,
  ...rest
}) {
  if (!product) return null;
  if (size !== 'largest') {
    return (
      <BaseProductImage
        product={product}
        size={size}
        className={className}
        {...rest}
      />
    );
  }

  const { bottleImage } = product;
  const hasImage = Boolean(bottleImage?.fixed);

  return (
    <div
      sx={{
        backgroundColor: 'background',
        position: 'relative',
        boxSizing: 'content-box',
        color: 'lighterText',
        ...dimensions[size],
      }}
      className={className}
    >
      {hasImage ? (
        <Image
          fixed={getFixedImage(bottleImage.fixed)}
          loading="auto"
          alt={product.name}
          style={{ width: null, height: null }}
          imgStyle={{ objectFit: null, objectPosition: null }}
          placeholderStyle={{
            objectFit: null,
            objectPosition: null,
            filter: 'blur(5px)',
          }}
          sx={{
            borderRadius: 'default',
            ...dimensions[size],
            ...positions[size],
          }}
          {...rest}
        />
      ) : (
        <BottleImagePlaceholder sx={{ width: '100%', height: '100%' }} />
      )}
    </div>
  );
}

export const fragments = graphql`
  fragment ProductImage_largest on Product {
    bottleImage {
      fixed(
        width: 480
        height: 1200
        crop: FIT
        gravity: NORTH
        background: "#FFF0"
      ) {
        src
        srcSet
        width
        height
        # base64
      }
    }
  }
`;

export const ProductImageFragment_largest = gql`
  fragment ProductImage_largest on Product {
    bottleImage {
      fixed(
        width: 480
        height: 1200
        crop: PAD
        gravity: NORTH
        background: "#FFF0"
      ) {
        src
        srcSet
        width
        height
        # base64
      }
    }
  }
`;
